import { useSyncExternalStore } from "react";

// In-memory-only auth state, written exclusively by the postMessage handshake
// (src/services/handshake.ts). The key must never touch a URL, DOM attribute,
// or any storage API — module state is the whole persistence story.

export interface EmbedAuth {
  /** The API-key-style credential received over postMessage. */
  apiKey: string;
  /**
   * The validated origin of the embedding page — taken from the `auth`
   * MessageEvent's `origin` (browser-asserted), never from
   * `document.referrer`. Sent to cortex as `X-Aiera-Parent-Origin`.
   */
  parentOrigin: string;
}

let auth: EmbedAuth | null = null;
const listeners = new Set<() => void>();

export function setEmbedAuth(next: EmbedAuth): void {
  auth = next;
  for (const listener of listeners) listener();
}

export function getEmbedAuth(): EmbedAuth | null {
  return auth;
}

export function subscribeEmbedAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useEmbedAuth(): EmbedAuth | null {
  return useSyncExternalStore(subscribeEmbedAuth, getEmbedAuth);
}

/** Test-only reset. */
export function clearEmbedAuth(): void {
  auth = null;
  for (const listener of listeners) listener();
}
